// src/services/projectHealth.ts
//
// Deterministic project health label. Pure formula over the already-computed
// project progress + execution summary — no AI, no scoring models. Returns a
// label plus human-readable reasons.

import type { Project } from '../types/api';

export type ProjectHealth = 'healthy' | 'watch' | 'at_risk';

export interface ProjectHealthResult {
  projectId: string;
  health: ProjectHealth;
  label: 'Healthy' | 'Watch' | 'At Risk';
  reasons: string[];
}

const LABEL: Record<ProjectHealth, ProjectHealthResult['label']> = {
  healthy: 'Healthy',
  watch: 'Watch',
  at_risk: 'At Risk',
};

export interface ProjectHealthOptions {
  today: string;
  /** Project-wide overdue tasks above this → At Risk. */
  overdueAtRisk?: number;
  /** Remaining advancement units considered "high" near the end date. */
  highRemaining?: number;
  /** "Near end date" window (days). */
  nearEndDays?: number;
}

/**
 * Map a project's deterministic signals to Healthy / Watch / At Risk.
 *
 * At Risk: behind pace, any blocked work, overdue tasks over threshold, or a
 *          lot of work remaining right before the deadline.
 * Watch:   some overdue work, a milestone behind, or progress not yet measurable
 *          while work remains.
 * Healthy: on track / ahead / completed with no overdue or blocked work.
 */
export function computeProjectHealth(
  project: Project,
  opts: ProjectHealthOptions,
): ProjectHealthResult {
  const { today, overdueAtRisk = 3, highRemaining = 5, nearEndDays = 7 } = opts;
  const exec = project.executionSummary;
  const prog = project.progress;
  const reasons: string[] = [];

  if (project.status === 'completed' || prog?.trackingStatus === 'completed') {
    return { projectId: project.id, health: 'healthy', label: 'Healthy', reasons: ['Project complete'] };
  }

  const overdue = exec?.overdueTasks ?? 0;
  const blocked = exec?.blockedTasks ?? 0;
  const remaining = exec?.remainingWorkItems ?? 0;
  const behind = prog?.trackingStatus === 'behind';

  const daysToEnd = project.endDate
    ? Math.round((new Date(`${project.endDate.split('T')[0]}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86_400_000)
    : null;
  const nearEnd = daysToEnd != null && daysToEnd >= 0 && daysToEnd <= nearEndDays;
  const milestonesBehind = (project.milestones ?? []).filter(
    (m) => m.status !== 'completed' && m.progress?.trackingStatus === 'behind',
  ).length;

  // ── At Risk ──
  const atRisk: string[] = [];
  if (behind) atRisk.push('Behind pace');
  if (blocked > 0) atRisk.push(`${blocked} blocked task${blocked > 1 ? 's' : ''}`);
  if (overdue > overdueAtRisk) atRisk.push(`${overdue} overdue tasks`);
  if (nearEnd && remaining > highRemaining) atRisk.push(`${remaining} work items left, ${daysToEnd}d to deadline`);
  if (atRisk.length > 0) {
    return { projectId: project.id, health: 'at_risk', label: LABEL.at_risk, reasons: atRisk };
  }

  // ── Watch ──
  const watch: string[] = [];
  if (overdue > 0) watch.push(`${overdue} overdue task${overdue > 1 ? 's' : ''}`);
  if (milestonesBehind > 0) watch.push(`${milestonesBehind} milestone${milestonesBehind > 1 ? 's' : ''} behind`);
  if (prog?.trackingStatus === 'no_data' && remaining > 0) watch.push('Progress not measurable yet');
  if (watch.length > 0) {
    return { projectId: project.id, health: 'watch', label: LABEL.watch, reasons: watch };
  }

  // ── Healthy ──
  if (prog?.trackingStatus === 'ahead') reasons.push('Ahead of pace');
  else reasons.push('On track');
  return { projectId: project.id, health: 'healthy', label: LABEL.healthy, reasons };
}
